import React, { useState, useEffect, useCallback } from "react";

import ModaleAnimee from "../../../Components/ModaleAnimee";
/**
 * LA CONFIGURATION DES E-BOOKS — catalogue, niveaux, ventes.
 *
 * ──────────────────────────────────────────────────────────────────────────────────────────
 * POURQUOI UN ONGLET À PART, ET NON UN SOUS-ONGLET DE « BOUTIQUE ».
 *
 * Les deux vendent, mais ils ne vendent pas la même chose et ne s'appuient pas sur les mêmes
 * tables. La boutique sert des produits — objets, PDF vendus seuls — depuis `produits` /
 * `commandes`, où un achat est relié à son client par une CHAÎNE DE CARACTÈRES comparée avec
 * `ilike()`. L'e-book, lui, est une addition : « un livre conservé définitivement + une
 * nouvelle expérience accompagnée de 30 jours », avec un achat relié au compte par une clé
 * étrangère. Les ranger sous le même écran laisserait croire qu'on peut les administrer de
 * la même façon.
 *
 * ── CE QUE CET ÉCRAN NE MONTRE PAS ENCORE ──
 * L'expérience de 30 jours : elle n'existe pas. Tant qu'elle n'est pas construite, le
 * catalogue se prépare mais rien ne se vend — ouvrir la vente reviendrait à encaisser le
 * prix de l'addition pour n'en livrer qu'un terme.
 *
 * ── LE COMPOSANT EST AUTONOME ──
 * Il charge, édite et enregistre lui-même. Le tableau de bord ne lui passe que de quoi
 * appeler l'API et savoir ce que la personne a le droit de faire. C'est le motif de
 * `PanneauConfidentialite`, et pour la même raison : le jour où un autre profil doit
 * administrer les e-books, il y a trois lignes à écrire et rien à dupliquer.
 * ──────────────────────────────────────────────────────────────────────────────────────────
 */

/* Les huit bandes du §11. EN DUR ICI, ET C'EST ASSUMÉ : ce n'est pas une règle métier, c'est
   la liste des cases à cocher d'un formulaire. Le lien bande → e-book, lui, vit en base
   (`ebooks_niveaux`) parce que le §22 interdit de toucher au code pour l'ouvrir. */
const BANDES = ["Pre-A1", "A1", "A1+", "Lower A2", "A2", "Lower B1", "B1", "B2"];

const STATUTS = [
  { cle: "brouillon", label: "Brouillon", aide: "Invisible du catalogue. Jamais proposé à la vente.",            ton: "#64748b", fond: "#f1f5f9" },
  { cle: "actif",     label: "En vente",  aide: "Proposé aux clients dont le niveau correspond.",                ton: "#15803d", fond: "#dcfce7" },
  { cle: "retire",    label: "Retiré",    aide: "Sorti de la vente. Les clients qui l'ont acheté le gardent.",   ton: "#b45309", fond: "#fef3c7" },
];

const VIDE = {
  code: "", titre: "", sous_titre: "", description: "",
  prix: 4900, devise: "XOF", couverture_url: "", pdf_url: "",
  statut: "brouillon", ordre: 0,
};

/* `couleur` VIENT DU TABLEAU DE BORD, qui la lit sur le profil de la personne connectée.
   Une valeur figée ici jurerait dès qu'un administrateur choisit une autre teinte — et ce
   panneau est désormais imbriqué dans l'onglet Boutique, juste à côté de sous-onglets qui,
   eux, suivent ce réglage. */
export default function PanneauEbooks({ apiUrl, authHeaders, peut = () => true, couleur = "#0891b2" }) {
  const [onglet, setOnglet]       = useState("catalogue");
  const [ebooks, setEbooks]       = useState([]);
  const [commandes, setCommandes] = useState([]);
  const [charge, setCharge]       = useState(true);
  const [erreur, setErreur]       = useState(null);

  const [edite, setEdite]   = useState(null);   // null = aucun formulaire ouvert
  const [enCours, setEnCours] = useState(false);
  const [message, setMessage] = useState(null); // { ton, texte }

  const peutEcrire = peut("eshop", "create") || peut("eshop", "update");

  const charger = useCallback(async () => {
    setCharge(true); setErreur(null);
    try {
      const r = await fetch(`${apiUrl}/api/eshop/ebooks`, { headers: authHeaders() });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `Erreur ${r.status}`);
      setEbooks(await r.json());
    } catch (e) {
      /* ON AFFICHE LA CAUSE, pas « une erreur est survenue ». La plus probable ici est que
         `eshop_socle.sql` n'ait pas été exécuté : le dire fait gagner une demi-heure. */
      setErreur(e.message);
    } finally {
      setCharge(false);
    }
  }, [apiUrl, authHeaders]);

  const chargerCommandes = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/api/eshop/commandes`, { headers: authHeaders() });
      if (r.ok) setCommandes(await r.json());
    } catch { /* le journal des ventes est secondaire : son absence ne doit rien bloquer */ }
  }, [apiUrl, authHeaders]);

  useEffect(() => { charger(); }, [charger]);
  useEffect(() => { if (onglet === "ventes") chargerCommandes(); }, [onglet, chargerCommandes]);

  /* ÉCHAP FERME LA MODALE. L'écouteur n'est posé QUE pendant qu'elle est ouverte : un
     écouteur permanent intercepterait la touche sur les autres onglets de la boutique, où
     elle ne doit rien faire. `enCours` le neutralise — fermer pendant l'enregistrement
     laisserait un e-book créé sans ses niveaux, sans que personne le sache. */
  useEffect(() => {
    if (!edite) return;
    const surTouche = (e) => { if (e.key === "Escape" && !enCours) setEdite(null); };
    window.addEventListener("keydown", surTouche);
    return () => window.removeEventListener("keydown", surTouche);
  }, [edite, enCours]);

  const signaler = (ton, texte) => {
    setMessage({ ton, texte });
    setTimeout(() => setMessage(null), 5000);
  };

  /* ── Enregistrer un e-book, puis ses niveaux ──
     DEUX APPELS, ET L'ORDRE COMPTE : les niveaux ont besoin de l'identifiant, qui n'existe
     qu'une fois l'e-book créé. En cas d'échec du second, le premier reste — l'e-book existe
     sans bande, donc invisible du catalogue, ce qui est l'échec le moins nuisible possible. */
  const enregistrer = async () => {
    if (!edite?.code?.trim() || !edite?.titre?.trim()) {
      return signaler("erreur", "Le code et le titre sont obligatoires.");
    }
    setEnCours(true);
    try {
      const creation = !edite.id;
      const r = await fetch(
        creation ? `${apiUrl}/api/eshop/ebooks` : `${apiUrl}/api/eshop/ebooks/${edite.id}`,
        {
          method: creation ? "POST" : "PATCH",
          headers: authHeaders(),
          body: JSON.stringify({
            code: edite.code.trim(), titre: edite.titre.trim(),
            sous_titre: edite.sous_titre || null, description: edite.description || null,
            prix: Number(edite.prix) || 0, devise: edite.devise || "XOF",
            couverture_url: edite.couverture_url || null, pdf_url: edite.pdf_url || null,
            statut: edite.statut, ordre: Number(edite.ordre) || 0,
          }),
        }
      );
      const corps = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corps.error || `Erreur ${r.status}`);

      const id = corps.id || edite.id;
      const rNiv = await fetch(`${apiUrl}/api/eshop/ebooks/${id}/niveaux`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify({
          niveaux: (edite.bandes || []).map((b, i) => ({ bande: b, ordre: i })),
        }),
      });
      if (!rNiv.ok) {
        const c = await rNiv.json().catch(() => ({}));
        throw new Error(`E-book enregistré, mais les niveaux ont échoué : ${c.error || rNiv.status}`);
      }

      signaler("ok", creation ? "E-book créé." : "Modifications enregistrées.");
      setEdite(null);
      charger();
    } catch (e) {
      signaler("erreur", e.message);
    } finally {
      setEnCours(false);
    }
  };

  const supprimer = async (e) => {
    if (!window.confirm(`Supprimer définitivement « ${e.titre} » ?`)) return;
    try {
      const r = await fetch(`${apiUrl}/api/eshop/ebooks/${e.id}`, {
        method: "DELETE", headers: authHeaders(),
      });
      const corps = await r.json().catch(() => ({}));
      /* 409 = l'e-book est dans des bibliothèques. Le serveur explique le bon geste —
         passer en « Retiré » — et ce message vaut mieux que n'importe lequel écrit ici. */
      if (!r.ok) throw new Error(corps.error || `Erreur ${r.status}`);
      signaler("ok", "E-book supprimé.");
      charger();
    } catch (err) {
      signaler("erreur", err.message);
    }
  };

  const ouvrir = (e) => setEdite(
    e ? { ...e, bandes: (e.ebooks_niveaux || []).sort((a, b) => a.ordre - b.ordre).map(n => n.bande) }
      : { ...VIDE, bandes: [] }
  );

  const basculerBande = (b) => setEdite(e => ({
    ...e,
    bandes: e.bandes.includes(b) ? e.bandes.filter(x => x !== b) : [...e.bandes, b],
  }));

  // ── Styles locaux, calés sur le reste du tableau de bord ───────────────────────────────
  const S = {
    champ:  { width:"100%", padding:"9px 12px", border:"1.5px solid #e2e8f0", borderRadius:8,
              fontSize:13, fontFamily:"inherit", color:"#0f172a", background:"#fff" },
    label:  { display:"block", fontSize:11, fontWeight:700, color:"#64748b",
              textTransform:"uppercase", letterSpacing:".04em", marginBottom:5 },
    bouton: { padding:"9px 16px", borderRadius:8, border:"none", fontWeight:700,
              fontSize:13, cursor:"pointer", fontFamily:"inherit" },
  };

  const statutDe = (cle) => STATUTS.find(s => s.cle === cle) || STATUTS[0];

  return (
    <div>
      {/* ── Bandeau de contexte ──
          Il dit à quoi sert cet écran AUJOURD'HUI. Sans lui, quelqu'un qui met un e-book
          « En vente » croirait l'avoir mis en vente — alors que le tunnel d'achat n'existe
          pas encore côté site. */}
      <div style={{ background:"#eff6ff", border:"1.5px solid #bfdbfe", borderRadius:10,
                    padding:"14px 18px", marginBottom:20 }}>
        <div style={{ fontWeight:800, color:"#1e40af", fontSize:13, marginBottom:4 }}>
          Le catalogue se prépare, la vente n'est pas ouverte
        </div>
        <div style={{ fontSize:12.5, color:"#1e3a8a", lineHeight:1.6 }}>
          Un e-book acheté donne <strong>un livre conservé définitivement</strong> et
          <strong> une nouvelle expérience accompagnée de 30 jours</strong>. La seconde moitié
          n'est pas encore construite : le statut « En vente » prépare le catalogue, mais
          aucun client ne peut acheter tant que l'expérience n'existe pas.
        </div>
      </div>

      {/* ── Sous-onglets ── */}
      <div style={{ display:"flex", gap:4, borderBottom:"2px solid #e2e8f0", marginBottom:20 }}>
        {[["catalogue", "📘 Catalogue", ebooks.length],
          ["ventes",    "🧾 Ventes",    commandes.length]].map(([cle, lab, n]) => (
          <button key={cle} onClick={() => setOnglet(cle)} style={{
            padding:"10px 18px", background:"none", border:"none", cursor:"pointer",
            fontSize:13, fontWeight:700, fontFamily:"inherit",
            color: onglet === cle ? couleur : "#6b7280",
            borderBottom: onglet === cle ? `3px solid ${couleur}` : "3px solid transparent",
            marginBottom:-2,
          }}>
            {lab}{n ? ` (${n})` : ""}
          </button>
        ))}
      </div>

      {message && (
        <div style={{
          padding:"11px 16px", borderRadius:8, marginBottom:16, fontSize:13, fontWeight:600,
          background: message.ton === "ok" ? "#dcfce7" : "#fee2e2",
          color:      message.ton === "ok" ? "#15803d" : "#b91c1c",
          border: `1px solid ${message.ton === "ok" ? "#86efac" : "#fecaca"}`,
        }}>{message.texte}</div>
      )}

      {/* ══════════════════ CATALOGUE ══════════════════ */}
      {onglet === "catalogue" && (
        <>
          {peutEcrire && (
            <button onClick={() => ouvrir(null)} style={{
              ...S.bouton, background:couleur, color:"#fff", marginBottom:18 }}>
              + Nouvel e-book
            </button>
          )}

          {/* ══ LA MODALE DE CRÉATION / MODIFICATION ══
              EN SUPERPOSITION PLUTÔT QU'EN DÉPLIÉ. Le formulaire poussait la liste vers le
              bas : sur un catalogue de plusieurs e-books, on perdait de vue celui qu'on
              venait de cliquer, et le bouton « Enregistrer » se retrouvait hors écran.
              `position:fixed` SUR L'ARRIÈRE-PLAN, et non `absolute` : le panneau est
              imbriqué dans l'onglet Boutique, lui-même dans une carte à `overflow:hidden`.
              Un positionnement relatif au parent se serait retrouvé rogné par cette carte.
              LE CLIC SUR LE FOND FERME, mais pas celui sur la fenêtre : `stopPropagation`
              sur le panneau évite qu'un clic dans un champ remonte jusqu'au fond et ferme
              un formulaire à moitié rempli. */}
          {edite && (
            <ModaleAnimee ouvert
              onClicVoile={() => setEdite(null)}
              style={{ position:"fixed", inset:0, background:"rgba(15,23,42,.55)",
                       display:"flex", alignItems:"flex-start", justifyContent:"center",
                       padding:"40px 20px", overflowY:"auto", zIndex:9000 }}
              panneauStyle={{ background:"#fff", border:"1.5px solid #cbd5e1", borderRadius:14,
                       padding:24, width:"100%", maxWidth:820,
                       boxShadow:"0 24px 60px rgba(2,10,40,.3)" }}>
              <div style={{ display:"flex", justifyContent:"space-between",
                            alignItems:"center", marginBottom:18 }}>
                <h4 style={{ margin:0, fontSize:16, fontWeight:800, color:"#0f172a" }}>
                  {edite.id ? `Modifier « ${edite.titre} »` : "Nouvel e-book"}
                </h4>
                <button onClick={() => setEdite(null)} aria-label="Fermer" style={{
                  background:"none", border:"none", cursor:"pointer",
                  fontSize:20, color:"#94a3b8", lineHeight:1, padding:4 }}>✕</button>
              </div>

              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:14 }}>
                <div>
                  <label style={S.label}>Code *</label>
                  <input style={S.champ} value={edite.code}
                    onChange={e => setEdite({ ...edite, code: e.target.value })}
                    placeholder="a1-essentiels" />
                  {/* Le code sert à retrouver l'e-book dans un tableur ou un e-mail : un
                      identifiant technique ne se dicte pas au téléphone. */}
                  <div style={{ fontSize:11, color:"#94a3b8", marginTop:4 }}>
                    Identifiant lisible et définitif. Doit être unique.
                  </div>
                </div>
                <div>
                  <label style={S.label}>Titre *</label>
                  <input style={S.champ} value={edite.titre}
                    onChange={e => setEdite({ ...edite, titre: e.target.value })} />
                </div>
                <div>
                  <label style={S.label}>Sous-titre</label>
                  <input style={S.champ} value={edite.sous_titre || ""}
                    onChange={e => setEdite({ ...edite, sous_titre: e.target.value })} />
                </div>
                <div>
                  <label style={S.label}>Prix (FCFA)</label>
                  <input style={S.champ} type="number" min="0" value={edite.prix}
                    onChange={e => setEdite({ ...edite, prix: e.target.value })} />
                </div>
                <div>
                  <label style={S.label}>Ordre d'affichage</label>
                  <input style={S.champ} type="number" value={edite.ordre}
                    onChange={e => setEdite({ ...edite, ordre: e.target.value })} />
                </div>
                <div>
                  <label style={S.label}>Couverture (URL)</label>
                  <input style={S.champ} value={edite.couverture_url || ""}
                    onChange={e => setEdite({ ...edite, couverture_url: e.target.value })} />
                </div>
                <div style={{ gridColumn:"1 / -1" }}>
                  <label style={S.label}>Fichier PDF (URL)</label>
                  <input style={S.champ} value={edite.pdf_url || ""}
                    onChange={e => setEdite({ ...edite, pdf_url: e.target.value })} />
                  <div style={{ fontSize:11, color:"#94a3b8", marginTop:4 }}>
                    Le lien n'est jamais donné au navigateur : le serveur vérifie que le client
                    possède le livre avant de le livrer.
                  </div>
                </div>
                <div style={{ gridColumn:"1 / -1" }}>
                  <label style={S.label}>Description</label>
                  <textarea style={{ ...S.champ, minHeight:80, resize:"vertical" }}
                    value={edite.description || ""}
                    onChange={e => setEdite({ ...edite, description: e.target.value })} />
                </div>
              </div>

              {/* ── Statut ── */}
              <div style={{ marginTop:20 }}>
                <label style={S.label}>Statut</label>
                <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                  {STATUTS.map(s => (
                    <button key={s.cle} onClick={() => setEdite({ ...edite, statut: s.cle })}
                      style={{
                        ...S.bouton, textAlign:"left", flex:"1 1 200px",
                        background: edite.statut === s.cle ? s.fond : "#fff",
                        color: s.ton,
                        border: `1.5px solid ${edite.statut === s.cle ? s.ton : "#e2e8f0"}`,
                        padding:"10px 14px",
                      }}>
                      <div style={{ fontSize:13, fontWeight:800 }}>{s.label}</div>
                      <div style={{ fontSize:11, fontWeight:500, opacity:.85, marginTop:3, lineHeight:1.45 }}>
                        {s.aide}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Niveaux ──
                  PLUSIEURS BANDES POUR UN MÊME E-BOOK, et plusieurs e-books pour une même
                  bande : la spec du test l'exige. D'où des cases à cocher et non un menu. */}
              <div style={{ marginTop:20 }}>
                <label style={S.label}>Niveaux auxquels il est proposé</label>
                <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                  {BANDES.map(b => {
                    const actif = (edite.bandes || []).includes(b);
                    return (
                      <button key={b} onClick={() => basculerBande(b)} style={{
                        ...S.bouton, padding:"7px 14px", fontSize:12.5,
                        background: actif ? couleur : "#fff",
                        color:      actif ? "#fff"    : "#475569",
                        border: `1.5px solid ${actif ? couleur : "#e2e8f0"}`,
                      }}>{b}</button>
                    );
                  })}
                </div>
                <div style={{ fontSize:11, color:"#94a3b8", marginTop:7, lineHeight:1.5 }}>
                  Sans aucun niveau coché, l'e-book n'apparaît dans aucun catalogue — quel que
                  soit son statut. L'ordre des clics détermine l'ordre d'affichage.
                </div>
              </div>

              <div style={{ display:"flex", gap:10, marginTop:22 }}>
                <button onClick={enregistrer} disabled={enCours} style={{
                  ...S.bouton, background:couleur, color:"#fff",
                  opacity: enCours ? .6 : 1, cursor: enCours ? "wait" : "pointer" }}>
                  {enCours ? "Enregistrement…" : "Enregistrer"}
                </button>
                <button onClick={() => setEdite(null)} style={{
                  ...S.bouton, background:"#fff", color:"#475569", border:"1.5px solid #e2e8f0" }}>
                  Annuler
                </button>
              </div>
            </ModaleAnimee>
          )}

          {/* ── Liste ── */}
          {charge && <div style={{ color:"#64748b", fontSize:13 }}>Chargement…</div>}

          {erreur && (
            <div style={{ background:"#fee2e2", border:"1px solid #fecaca", borderRadius:10,
                          padding:"14px 18px", color:"#b91c1c", fontSize:13 }}>
              <strong>Catalogue illisible.</strong> {erreur}
              <div style={{ marginTop:6, fontSize:12, opacity:.9 }}>
                Si le message parle d'une table absente, c'est que
                <code style={{ margin:"0 4px" }}>Backend/sql/eshop_socle.sql</code>
                n'a pas encore été exécuté.
              </div>
            </div>
          )}

          {!charge && !erreur && !ebooks.length && (
            <div style={{ textAlign:"center", padding:"50px 20px", color:"#94a3b8" }}>
              <div style={{ fontSize:38, marginBottom:10 }}>📘</div>
              <div style={{ fontWeight:700, color:"#475569", marginBottom:4 }}>Aucun e-book</div>
              <div style={{ fontSize:13 }}>Créez le premier pour commencer à préparer le catalogue.</div>
            </div>
          )}

          <div style={{ display:"grid", gap:12 }}>
            {ebooks.map(e => {
              const st = statutDe(e.statut);
              const bandes = (e.ebooks_niveaux || []).sort((a, b) => a.ordre - b.ordre);
              return (
                <div key={e.id} style={{
                  background:"#fff", border:"1.5px solid #e2e8f0", borderRadius:11,
                  padding:"16px 18px", display:"flex", gap:16, alignItems:"flex-start" }}>
                  {e.couverture_url
                    ? <img src={e.couverture_url} alt="" style={{ width:52, height:70, objectFit:"cover", borderRadius:6, flexShrink:0 }} />
                    : <div style={{ width:52, height:70, borderRadius:6, background:"#f1f5f9",
                                    display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>📘</div>}

                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                      <span style={{ fontWeight:800, color:"#0f172a", fontSize:14.5 }}>{e.titre}</span>
                      <span style={{ background:st.fond, color:st.ton, padding:"2px 10px",
                                     borderRadius:999, fontSize:11, fontWeight:700 }}>{st.label}</span>
                      <span style={{ fontFamily:"monospace", fontSize:11.5, color:"#94a3b8" }}>{e.code}</span>
                    </div>
                    {e.sous_titre && (
                      <div style={{ fontSize:12.5, color:"#64748b", marginTop:3 }}>{e.sous_titre}</div>
                    )}
                    <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:9 }}>
                      {bandes.length
                        ? bandes.map(n => (
                            <span key={n.id} style={{ background:"#e0f2fe", color:"#075985",
                              padding:"2px 9px", borderRadius:999, fontSize:11, fontWeight:700 }}>{n.bande}</span>
                          ))
                        : <span style={{ color:"#dc2626", fontSize:11.5, fontWeight:700 }}>
                            ⚠ aucun niveau — invisible du catalogue
                          </span>}
                    </div>
                  </div>

                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <div style={{ fontWeight:800, color:"#0f172a", fontSize:15 }}>
                      {Number(e.prix).toLocaleString("fr-FR")} <span style={{ fontSize:11, color:"#94a3b8" }}>{e.devise}</span>
                    </div>
                    {!e.pdf_url && (
                      <div style={{ fontSize:11, color:"#b45309", fontWeight:700, marginTop:5 }}>
                        ⚠ pas de PDF
                      </div>
                    )}
                    {peutEcrire && (
                      <div style={{ display:"flex", gap:7, marginTop:11, justifyContent:"flex-end" }}>
                        <button onClick={() => ouvrir(e)} style={{
                          ...S.bouton, padding:"6px 12px", fontSize:12,
                          background:"#fff", color:couleur, border:`1.5px solid ${couleur}` }}>
                          Modifier
                        </button>
                        {peut("eshop", "delete") && (
                          <button onClick={() => supprimer(e)} style={{
                            ...S.bouton, padding:"6px 12px", fontSize:12,
                            background:"#fff", color:"#dc2626", border:"1.5px solid #fecaca" }}>
                            Supprimer
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ══════════════════ VENTES ══════════════════ */}
      {onglet === "ventes" && (
        !commandes.length ? (
          <div style={{ textAlign:"center", padding:"50px 20px", color:"#94a3b8" }}>
            <div style={{ fontSize:38, marginBottom:10 }}>🧾</div>
            <div style={{ fontWeight:700, color:"#475569", marginBottom:4 }}>Aucune vente</div>
            <div style={{ fontSize:13 }}>
              C'est attendu : le tunnel d'achat n'est pas encore ouvert côté site.
            </div>
          </div>
        ) : (
          <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead>
                <tr style={{ borderBottom:"2px solid #e2e8f0" }}>
                  {["Date", "Client", "E-book", "Montant", "Statut", "Référence"].map(h => (
                    <th key={h} style={{ textAlign:"left", padding:"0 12px 9px 0", fontSize:11,
                      fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:".04em" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {commandes.map(c => (
                  <tr key={c.id} style={{ borderBottom:"1px solid #f1f5f9" }}>
                    <td style={{ padding:"11px 12px 11px 0", color:"#64748b" }}>
                      {new Date(c.created_at).toLocaleDateString("fr-FR")}
                    </td>
                    <td style={{ padding:"11px 12px 11px 0", fontWeight:600 }}>
                      {c.utilisateurs ? `${c.utilisateurs.prenom || ""} ${c.utilisateurs.nom || ""}`.trim() || c.utilisateurs.email : "—"}
                    </td>
                    <td style={{ padding:"11px 12px 11px 0" }}>{c.ebooks?.titre || "—"}</td>
                    <td style={{ padding:"11px 12px 11px 0", fontWeight:700 }}>
                      {Number(c.montant).toLocaleString("fr-FR")} {c.devise}
                    </td>
                    <td style={{ padding:"11px 12px 11px 0" }}>{c.statut}</td>
                    <td style={{ padding:"11px 12px 11px 0", fontFamily:"monospace", fontSize:11.5, color:"#94a3b8" }}>
                      {c.order_number || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
